import {
  Card,
  CardBody,
  CardHeader,
  Flex,
  HStack,
  StackDivider,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  StyleProps,
} from "@chakra-ui/react";
import { useCapitalActivitySummaryChartRetrieveSuspense } from "gen/endpoints/capital-activity/capital-activity";
import { useTranslation } from "react-i18next";
import { MaybernChartWrapper } from "shared/components/chart/MaybernChartWrapper";
import { chartStatFormatters } from "shared/components/chart/utils";
import colors from "theme/colors";
import { textStyles } from "theme/textStyles";

export const FundFamilySummaryChartCapitalStatus: React.FC<
  StyleProps & {
    fundFamilyId: string;
  }
> = ({ fundFamilyId, ...props }) => {
  const { data: chartData } =
    useCapitalActivitySummaryChartRetrieveSuspense(fundFamilyId);
  const { t } = useTranslation(["fundFamily"]);

  return (
    <Card variant="chart" {...props}>
      <CardHeader>{t("fundFamily:summary.charts.status.title")}</CardHeader>
      <CardBody>
        <Flex flex={1} justifyContent="center">
          <Flex
            height="250px"
            maxWidth="380px"
            minWidth={0}
            flex={1}
            justifyContent="center"
          >
            <MaybernChartWrapper
              palette={{
                fills: [colors.cyan[600], colors.gray[100]],
                strokes: [colors.cyan[600], colors.gray[100]],
              }}
              seriesConfig={{
                tooltipKey: "capital_activity",
                // @ts-expect-error type definitions incorrectly missing innerLabels.fontSize etc
                innerLabels: chartData.stats?.length
                  ? [
                      {
                        text: chartData.stats[0].label,
                        margin: 4,
                        fontSize: textStyles.small.fontSize,
                        color: colors.gray[500],
                      },
                      {
                        text: chartData.stats[0].stat?.value
                          ? chartStatFormatters[chartData.stats[0].stat.type](
                              chartData.stats[0].stat as never,
                            )
                          : "",
                        fontSize: textStyles.h5.fontSize,
                        fontWeight: textStyles.h5.fontWeight,
                        color: colors.gray[800],
                      },
                    ]
                  : [],
              }}
              {...chartData}
            />
          </Flex>
        </Flex>
        <HStack
          divider={<StackDivider />}
          mt="12px"
          spacing="24px"
          sx={{
            borderTopWidth: "1px",
            px: "24px",
            "& > .chakra-stat__group": {
              justifyContent: "center",
              gap: "10px",
              py: "20px",
              ".chakra-stat": {
                py: 0,
              },
            },
          }}
        >
          {chartData.stats?.slice(1).map(
            (
              item, // in this chart, the first stat is being used as the inner label
            ) => (
              <StatGroup flex={3} key={item.label}>
                {item.stat?.value && (
                  <Stat size="md">
                    <StatNumber color="gray.500">
                      {chartStatFormatters[item.stat.type](item.stat as never)}
                    </StatNumber>
                  </Stat>
                )}
                <Stat size="xs">
                  <StatLabel>{item.label}</StatLabel>
                  <StatNumber>
                    {chartStatFormatters[item.sub_stat!.type](
                      item.sub_stat! as never,
                    )}
                  </StatNumber>
                </Stat>
              </StatGroup>
            ),
          )}
        </HStack>
      </CardBody>
    </Card>
  );
};
